import axios from "axios";

/*
 * The places a spare part can sit: /api/storage-locations.
 *
 * A controlled vocabulary, not a second inventory dimension: a location has no balance of its
 * own, and there are no transfers between them. Stock stays the sum of a part's movements,
 * company-wide, which is why nothing here touches /api/spare-parts.
 *
 * Shaped like cmmsSparePart.jsx, including throwResponseData — the CMMS controllers answer a
 * failure with ApiErrorResponse { code, message }, and rethrowing the body rather than the axios
 * error is what lets formatCmmsError print "A storage location named 'A-12' already exists for
 * this company" instead of "Request failed with status code 400". On a duplicate name that
 * sentence is the entire message: it names the row already holding it.
 *
 * Nothing here sends a user id. The server takes "who did this" from the token and overwrites
 * anything a request supplied, so passing one would only suggest it mattered.
 */
const baseUrl = "/api/storage-locations";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const getStorageLocations = async (companyKey, includeInactive = false) => {
  try {
    const response = await axios.get(baseUrl, { params: { companyKey, includeInactive } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * No caller yet. Kept because it is the sixth member of the service contract, and a fetcher
 * missing one of six reads as though it maps a different API than it does.
 */
export const getStorageLocationById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createStorageLocation = async (storageLocation) => {
  try {
    const response = await axios.post(baseUrl, storageLocation);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * A rename, in place. The body carries no isActive: retiring a location is a decision of its own
 * with its own endpoint, not something an edit to the name should be able to do in passing.
 */
export const updateStorageLocation = async (id, storageLocation) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}`, storageLocation);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Retire, not delete. There is no DELETE endpoint on purpose: spare parts name these rows, and
 * removing one would leave every part on that shelf with no way to say where it is.
 *
 * Deactivating is never refused, however many parts sit there. They keep naming the location and
 * keep resolving its name; what stops is it being offered as a choice for anything new.
 *
 * Answers 204, so response.data is empty — returned anyway to keep the six shaped alike.
 */
export const deactivateStorageLocation = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/deactivate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const activateStorageLocation = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/activate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
